<template>
    <div class="repayment-details">
        <!-- Loading State -->
        <div v-if="loading" class="tw-p-2 tw-text-center">
            <Icon type="ios-loading" size="20" class="spin-icon tw-text-blue-600" />
            <p class="tw-mt-1 tw-text-xs tw-text-gray-600">Loading...</p>
        </div>

        <!-- No Data State -->
        <div v-else-if="!details || !details.repayments || details.repayments.length === 0" class="tw-p-3 tw-text-center tw-text-gray-500 tw-text-xs">
            <Icon type="ios-information-circle-outline" size="20" />
            <p>No repayment data</p>
        </div>

        <!-- Data Display -->
        <div v-else class="tw-p-2 tw-bg-gray-50">
            <!-- Header -->
            <div class="tw-flex tw-justify-between tw-items-center tw-mb-2 tw-pb-1 tw-border-b tw-border-blue-200">
                <h4 class="tw-font-semibold tw-text-xs tw-flex tw-items-center">
                    <Icon type="ios-list-box" size="14" class="tw-mr-1 tw-text-blue-600" />
                    <span>Repayments</span>
                    <Tag color="blue" class="tw-ml-1">{{ details.repayments.length }}</Tag>
                </h4>
                <div class="tw-text-right">
                    <div class="tw-text-xs tw-text-gray-600">Total</div>
                    <div class="tw-text-green-600 tw-font-bold tw-text-sm">{{ formatNumber(details.totals.total_amount) }}</div>
                </div>
            </div>

            <!-- Summary Cards -->
            <div class="tw-grid tw-grid-cols-4 tw-gap-1 tw-mb-2">
                <div class="tw-bg-white tw-p-1 tw-rounded tw-shadow-sm tw-border tw-border-blue-100">
                    <div class="tw-text-xs tw-text-gray-600">Principal</div>
                    <div class="tw-font-semibold tw-text-xs tw-text-blue-600 tw-text-right">{{ formatNumber(details.totals.total_principal) }}</div>
                </div>
                <div class="tw-bg-white tw-p-1 tw-rounded tw-shadow-sm tw-border tw-border-purple-100">
                    <div class="tw-text-xs tw-text-gray-600">Interest</div>
                    <div class="tw-font-semibold tw-text-xs tw-text-purple-600 tw-text-right">{{ formatNumber(details.totals.total_interest) }}</div>
                </div>
                <div class="tw-bg-white tw-p-1 tw-rounded tw-shadow-sm tw-border tw-border-orange-100">
                    <div class="tw-text-xs tw-text-gray-600">Penalty</div>
                    <div class="tw-font-semibold tw-text-xs tw-text-orange-600 tw-text-right">{{ formatNumber(details.totals.total_penalty) }}</div>
                </div>
                <div class="tw-bg-white tw-p-1 tw-rounded tw-shadow-sm tw-border tw-border-green-100">
                    <div class="tw-text-xs tw-text-gray-600">Fee</div>
                    <div class="tw-font-semibold tw-text-xs tw-text-green-600 tw-text-right">{{ formatNumber(details.totals.total_fee) }}</div>
                </div>
            </div>

            <!-- Ultra Compact Repayments Table -->
            <div class="tw-overflow-x-auto tw-rounded tw-shadow">
                <table class="tw-w-full tw-text-xs tw-bg-white tw-border-collapse">
                    <thead>
                        <tr class="tw-bg-blue-600 tw-text-white">
                            <th class="tw-px-1 tw-py-1 tw-text-left tw-font-semibold tw-text-xs tw-whitespace-nowrap tw-text-gray-600">#</th>
                            <th class="tw-px-1 tw-py-1 tw-text-left tw-font-semibold tw-text-xs tw-whitespace-nowrap tw-text-gray-600">Customer</th>
                            <th class="tw-px-1 tw-py-1 tw-text-left tw-font-semibold tw-text-xs tw-whitespace-nowrap tw-text-gray-600">Loan</th>
                            <th class="tw-px-1 tw-py-1 tw-text-left tw-font-semibold tw-text-xs tw-whitespace-nowrap tw-text-gray-600">Payment Methods</th>
                            <th class="tw-px-1 tw-py-1 tw-text-right tw-font-semibold tw-text-xs tw-whitespace-nowrap tw-text-gray-600">Principal</th>
                            <th class="tw-px-1 tw-py-1 tw-text-right tw-font-semibold tw-text-xs tw-whitespace-nowrap tw-text-gray-600">Interest</th>
                            <th class="tw-px-1 tw-py-1 tw-text-right tw-font-semibold tw-text-xs tw-whitespace-nowrap tw-text-gray-600">Penalty</th>
                            <th class="tw-px-1 tw-py-1 tw-text-right tw-font-semibold tw-text-xs tw-whitespace-nowrap tw-text-gray-600">Fee</th>
                            <th class="tw-px-1 tw-py-1 tw-text-right tw-font-semibold tw-text-xs tw-whitespace-nowrap tw-text-gray-600">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr 
                            v-for="(rep, index) in details.repayments" 
                            :key="rep.loan_id"
                            :class="index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50'"
                            class="tw-border-b tw-border-gray-200 hover:tw-bg-blue-50 tw-transition-colors"
                        >
                            <td class="tw-px-1 tw-py-1 tw-text-gray-600">{{ index + 1 }}</td>
                            <td class="tw-px-1 tw-py-1">
                                <div class="tw-font-medium tw-text-xs tw-leading-tight">{{ rep.customer_name }}</div>
                                <div v-if="rep.customer_name_kh" class="tw-text-xs tw-text-gray-500 tw-leading-tight">{{ rep.customer_name_kh }}</div>
                            </td>
                            <td class="tw-px-1 tw-py-1">
                                <span class="tw-font-mono tw-text-xs tw-bg-gray-100 tw-px-1 tw-rounded">{{ rep.loan_code }}</span>
                            </td>
                            <td class="tw-px-1 tw-py-1">
                                <div v-if="rep.payment_methods && rep.payment_methods.length > 0" class="tw-space-y-0.5">
                                    <div v-for="(pm, pmIndex) in rep.payment_methods" :key="pmIndex" 
                                         class="tw-flex tw-items-center tw-justify-between tw-text-xs tw-leading-tight">
                                        <div class="tw-flex tw-items-center tw-space-x-1">
                                            <Tag :color="getPaymentMethodColor(pm.repayment_method_id)">
                                                {{ pm.repayment_method_name }}
                                            </Tag>
                                            <span v-if="pm.bank_name" class="tw-text-gray-600 tw-text-xs">{{ pm.bank_name }}</span>
                                        </div>
                                        <span class="tw-font-semibold tw-whitespace-nowrap tw-ml-2">{{ pm.currency_code }} {{ formatNumber(pm.amount) }}</span>
                                    </div>
                                </div>
                                <span v-else class="tw-text-gray-400 tw-text-xs">-</span>
                            </td>
                            <td class="tw-px-1 tw-py-1 tw-text-right tw-text-xs tw-whitespace-nowrap">{{ formatNumber(rep.principal_paid) }}</td>
                            <td class="tw-px-1 tw-py-1 tw-text-right tw-text-xs tw-whitespace-nowrap">{{ formatNumber(rep.interest_paid) }}</td>
                            <td class="tw-px-1 tw-py-1 tw-text-right tw-text-xs tw-whitespace-nowrap">
                                <span v-if="rep.overdue_penalty_paid > 0" class="tw-text-orange-600">{{ formatNumber(rep.overdue_penalty_paid) }}</span>
                                <span v-else class="tw-text-gray-400">-</span>
                            </td>
                            <td class="tw-px-1 tw-py-1 tw-text-right tw-text-xs tw-whitespace-nowrap">
                                <span v-if="rep.monthly_fee_paid > 0">{{ formatNumber(rep.monthly_fee_paid) }}</span>
                                <span v-else class="tw-text-gray-400">-</span>
                            </td>
                            <td class="tw-px-1 tw-py-1 tw-text-right tw-font-bold tw-text-xs tw-text-green-600 tw-whitespace-nowrap">{{ formatNumber(rep.actual_payment_amount) }}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr class="tw-font-bold tw-text-xs tw-bg-gray-100 tw-border-t-2 tw-border-gray-300">
                            <td colspan="4" class="tw-px-1 tw-py-1 tw-text-left tw-uppercase tw-text-gray-700">Total ({{ details.repayments.length }})</td>
                            <td class="tw-px-1 tw-py-1 tw-text-right tw-text-blue-600 tw-whitespace-nowrap">{{ formatNumber(details.totals.total_principal) }}</td>
                            <td class="tw-px-1 tw-py-1 tw-text-right tw-text-purple-600 tw-whitespace-nowrap">{{ formatNumber(details.totals.total_interest) }}</td>
                            <td class="tw-px-1 tw-py-1 tw-text-right tw-text-orange-600 tw-whitespace-nowrap">{{ formatNumber(details.totals.total_penalty) }}</td>
                            <td class="tw-px-1 tw-py-1 tw-text-right tw-text-green-600 tw-whitespace-nowrap">{{ formatNumber(details.totals.total_fee) }}</td>
                            <td class="tw-px-1 tw-py-1 tw-text-right tw-text-green-700 tw-text-sm tw-bg-green-100 tw-whitespace-nowrap">{{ formatNumber(details.totals.total_amount) }}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    name: 'RepaymentDetailsRow',
    props: {
        details: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    methods: {
        formatNumber(num) {
            if ((!num && num !== 0) || num === 0) return '-';
            return new Intl.NumberFormat('en-US', { 
                minimumFractionDigits: 0, 
                maximumFractionDigits: 2 
            }).format(num);
        },
        formatDate(date) {
            return date ? moment(date).format('DD-MMM-YYYY') : '-';
        },
        getPaymentMethodColor(methodId) {
            // 1 = Cash, 2 = Bank Transfer, etc.
            const colors = {
                1: 'green',   // Cash
                2: 'blue',    // Bank Transfer
                3: 'purple',  // Other
            };
            return colors[methodId] || 'default';
        }
    }
};
</script>

<style scoped>
.repayment-details {
    animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.spin-icon {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

/* Ensure table takes full width */
table {
    table-layout: auto;
    width: 100%;
}

/* Make sure text doesn't wrap */
th, td {
    white-space: nowrap;
}
</style>
